import { z } from "zod";
import {
  optionalContactCustomFieldsFilterSchema,
  optionalOutcomeFilterSchema,
  optionalPartialDateFilterSchema,
  optionalPlaybookCriteriaFilterSchema,
  optionalTeammatesFilterSchema
} from "../agents/customerInteractionFilters.js";
import { playbookStatsFiltersSchema } from "../agents/playbookStatsFilters.js";
import { selectedPlaybookSchema } from "../agents/selectedPlaybook.js";
import { accountIdSchema, asAccountId } from "./account.js";
import { asCompanyId } from "./company.js";
import { DEFAULT_DATE_RANGE_DAYS } from "./dateRange.js";
import { dateRangeSchema } from "./filters.js";
import { lenientDateRangeSchema } from "./lenientDateRange.js";
import { outcomeToolOutputSchema } from "./outcome.js";
import {
  asPlaybookId,
  playbookIdSchema,
  playbookNodeIdSchema
} from "./playbook.js";
import { asTeammateId, teammateIdSchema } from "./teammate.js";
const COPILOT_ID = "researchAgent";
function getMastraThreadLangfuseSessionId(agentId, threadId) {
  return `${agentId}_mastra_thread_${threadId}`;
}
function parseCopilotContent(text) {
  const paragraphs = text.split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p.length > 0);
  return {
    paragraphs: paragraphs.map((paragraphText) => ({
      fragments: parseParagraphFragments(paragraphText)
    }))
  };
}
function parseParagraphFragments(text) {
  const fragments = [];
  let remaining = text;
  while (remaining.length > 0) {
    const quoteMatch = remaining.match(
      /<quote\s+teammateId="([^"]+)"\s+accountId="([^"]+)"(?:\s+playbookId="([^"]+)")?>([\s\S]*?)<\/quote>/
    );
    const teammateAccountMatch = remaining.match(
      /<teammate_account\s+teammateId="([^"]+)"\s+accountId="([^"]+)"(?:\s+playbookId="([^"]+)")?>([\s\S]*?)<\/teammate_account>/
    );
    const teammateMatch = remaining.match(
      /<teammate\s+id="([^"]+)">([\s\S]*?)<\/teammate>/
    );
    const quoteIndex = quoteMatch ? remaining.indexOf(quoteMatch[0]) : -1;
    const teammateAccountIndex = teammateAccountMatch ? remaining.indexOf(teammateAccountMatch[0]) : -1;
    const teammateIndex = teammateMatch ? remaining.indexOf(teammateMatch[0]) : -1;
    let nextTagIndex = -1;
    let nextMatch = null;
    let tagType = null;
    const indices = [
      { index: quoteIndex, match: quoteMatch, type: "quote" },
      {
        index: teammateAccountIndex,
        match: teammateAccountMatch,
        type: "teammate_account"
      },
      {
        index: teammateIndex,
        match: teammateMatch,
        type: "teammate"
      }
    ].filter((item) => item.index !== -1).sort((a, b) => a.index - b.index);
    const earliest = indices[0];
    if (earliest !== void 0) {
      nextTagIndex = earliest.index;
      nextMatch = earliest.match;
      tagType = earliest.type;
    }
    if (nextMatch && nextTagIndex !== -1 && tagType) {
      const beforeText = remaining.slice(0, nextTagIndex);
      if (beforeText) {
        fragments.push({ type: "text", text: beforeText });
      }
      const [fullMatch, ...captures] = nextMatch;
      if (tagType === "quote") {
        const [teammateId, accountId, playbookId, tagText] = captures;
        if (teammateId === void 0 || accountId === void 0 || tagText === void 0) {
          throw new Error("Malformed quote tag in copilot content.");
        }
        fragments.push({
          type: "quote",
          teammateId: asTeammateId(teammateId),
          accountId: asAccountId(accountId),
          playbookId: playbookId ? asPlaybookId(playbookId) : void 0,
          text: tagText
        });
      } else if (tagType === "teammate_account") {
        const [teammateId, accountId, playbookId, tagText] = captures;
        if (teammateId === void 0 || accountId === void 0 || tagText === void 0) {
          throw new Error("Malformed teammate_account tag in copilot content.");
        }
        fragments.push({
          type: "teammate_account",
          teammateId: asTeammateId(teammateId),
          accountId: asAccountId(accountId),
          playbookId: playbookId ? asPlaybookId(playbookId) : void 0,
          text: tagText
        });
      } else if (tagType === "teammate") {
        const [teammateId, tagText] = captures;
        if (teammateId === void 0 || tagText === void 0) {
          throw new Error("Malformed teammate tag in copilot content.");
        }
        fragments.push({
          type: "teammate",
          teammateId: asTeammateId(teammateId),
          text: tagText
        });
      }
      remaining = remaining.slice(nextTagIndex + fullMatch.length);
    } else {
      if (remaining) {
        fragments.push({ type: "text", text: remaining });
      }
      break;
    }
  }
  return fragments;
}
const searchTeammatesToolInputSchema = z.object({
  search_term: z.string().min(1).describe(
    "Name or email fragment to search for. Wildcards are not supported."
  )
});
const searchTeammatesToolOutputSchema = z.object({
  teammates: z.array(
    z.object({
      id: z.uuid().describe(
        "Teammate ID (UUID intended to be used only in tool calls and tag attributes)"
      ),
      email: z.string().describe("Teammate email"),
      first_name: z.string().optional().describe("Teammate first name"),
      last_name: z.string().optional().describe("Teammate last name"),
      scores: z.object({
        playbook_adherence: z.string().describe("The teammate Playbook Adherence details"),
        talk_ratio: z.string().describe("The teammate Talk Ratio details"),
        customer_experience_score: z.string().describe("The teammate customer experience (CX) score details"),
        vocal_intelligence: z.string().describe(
          "The teammate vocal intelligence dimension scores (confidence, energy, rapport)"
        )
      }).optional().describe("The teammate global scores details")
    })
  ).describe(
    "The list of teammates that match the search term. Each record includes teammate details and their average scores."
  )
});
const getCriterionDetailsToolInputSchema = z.object({
  criterion_id: z.uuid().describe(
    "Playbook criterion ID (UUID intended to be used only in tool calls and tag attributes)"
  ),
  date_range: lenientDateRangeSchema.optional().describe(
    `The period for the score distribution and team-level statistics; the outcome-correlation fields ignore it. Defaults to the last ${DEFAULT_DATE_RANGE_DAYS} days.`
  ),
  filters: playbookStatsFiltersSchema
});
const criterionDistributionValueSchema = z.object({
  count: z.number(),
  percentage: z.number()
});
const criterionHealthSignalsSchema = z.object({
  score_distribution: z.object({
    date_range: dateRangeSchema,
    requested_date_range: dateRangeSchema,
    total_interactions: z.number(),
    graded_interactions: z.number(),
    ungraded_or_not_applicable_interactions: z.number(),
    values: z.record(z.string(), criterionDistributionValueSchema)
  }),
  team_distribution: z.object({
    teammate_count: z.number(),
    average_score: z.number().nullable(),
    median_score: z.number().nullable(),
    standard_deviation: z.number().nullable(),
    buckets: z.record(z.string(), criterionDistributionValueSchema)
  })
});
const criterionOutcomeCorrelationSchema = z.object({
  rate_label: z.string(),
  impact_label: z.string(),
  is_high_priority: z.boolean(),
  auc: z.number(),
  grading_rate: z.number(),
  estimated_impact_sentence: z.string()
});
const getCriterionDetailsToolOutputSchema = z.object({
  playbook: selectedPlaybookSchema.optional().describe(
    "Which playbook this criterion is from; set only when the agent is scoped to several playbooks."
  ),
  criterion: z.object({
    id: playbookNodeIdSchema,
    name: z.string(),
    section_path: z.array(z.string()),
    grading_context: z.string().optional(),
    grading_value_type: z.enum(["yes_no", "1_5_score"])
  }).nullable().describe("Structured criterion metadata, or null if not found"),
  health_signals: criterionHealthSignalsSchema.nullable().describe("Structured diagnostic performance signals for this criterion"),
  outcome_correlation: criterionOutcomeCorrelationSchema.nullable().describe(
    "Structured outcome correlation stats (AUC, grading rate, estimated impact) for this criterion. Always computed over a fixed trailing window independent of date_range \u2014 treat as present-day calibration, not stats for the requested period."
  )
});
const getSignalDetailsToolInputSchema = z.object({
  signal_id: z.uuid().describe(
    "Signal ID (UUID intended to be used only in tool calls and tag attributes)"
  )
});
const getSignalDetailsToolOutputSchema = z.object({
  signal_details: z.string().nullable().describe(
    "Markdown-formatted string of the signal's full description and metadata schema, or null if the signal id is unknown"
  ),
  reason: z.string().optional().describe("Why signal_details is null; absent on a successful lookup.")
});
const listCustomerInteractionsToolMaxPaginationLimit = 25;
const listCustomerInteractionsToolDefaultPaginationLimit = 10;
const optionalPaginationSchema = z.object({
  cursor: z.string().nullable().describe(
    "Cursor to the next page of customer interactions, or null to start from beginning."
  ),
  limit: z.number().min(1).max(listCustomerInteractionsToolMaxPaginationLimit).nullable().transform((v) => v ?? listCustomerInteractionsToolDefaultPaginationLimit).describe(
    "Maximum number of customer interactions to retrieve, or null for a reasonable default."
  )
}).nullish().transform(
  (v) => v ?? {
    limit: listCustomerInteractionsToolDefaultPaginationLimit
  }
).describe("Pagination options");
const optionalTranscriptAnalysisSchema = z.string().min(1).nullable().describe(
  "Short question to ask about each interaction's transcripts, or null to skip analysis. When provided, AI will analyze transcripts and include the answer in each interaction's results."
);
const listCustomerInteractionsFiltersSchema = z.object({
  date: optionalPartialDateFilterSchema,
  teammate_ids: optionalTeammatesFilterSchema,
  playbook_criteria: optionalPlaybookCriteriaFilterSchema,
  custom_fields: optionalContactCustomFieldsFilterSchema,
  outcome: optionalOutcomeFilterSchema
}).nullish().describe(
  "AND filters to refine the customer interaction list, or null for no filters."
);
const listCustomerInteractionsToolInputSchema = z.object({
  filters: listCustomerInteractionsFiltersSchema,
  pagination: optionalPaginationSchema,
  transcript_analysis_to_perform: optionalTranscriptAnalysisSchema
});
const listTeammateCustomerInteractionsFiltersSchema = z.object({
  date: optionalPartialDateFilterSchema,
  playbook_criteria: optionalPlaybookCriteriaFilterSchema,
  custom_fields: optionalContactCustomFieldsFilterSchema,
  outcome: optionalOutcomeFilterSchema
}).nullish().describe(
  "AND filters to refine the customer interaction list, or null for no filters."
);
const listTeammateCustomerInteractionsToolInputSchema = z.object({
  filters: listTeammateCustomerInteractionsFiltersSchema,
  pagination: optionalPaginationSchema,
  transcript_analysis_to_perform: optionalTranscriptAnalysisSchema
});
const itemSchema = z.object({
  interaction_id: z.uuid().describe(
    "Customer interaction ID (UUID intended to be used only in tool calls and tag attributes)"
  ),
  teammate: z.object({
    teammate_id: teammateIdSchema,
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    email: z.string()
  }),
  account_id: accountIdSchema.describe("Identifier used by the system"),
  interaction_contact_name: z.string().optional().nullable().describe("Customer name"),
  playbook_id: playbookIdSchema.describe("Identifier used by the system"),
  score_details: z.object({
    playbook_adherence: z.number().nullable(),
    talk_ratio: z.number().nullable()
  }),
  playbook_grading: z.string(),
  call_outline: z.string().nullable(),
  outcome: outcomeToolOutputSchema,
  transcript_analysis: z.string().nullable().optional()
});
const listCustomerInteractionsToolOutputSchema = z.object({
  customer_interactions: z.array(itemSchema).describe("List of customer interactions"),
  pagination: z.object({
    total_customer_interaction_count: z.number().describe(
      "Total number of customer interactions matching the filters across all pages (approximate)"
    ),
    total_page_count: z.number().describe(
      "Total number of pages of customer interactions matching the filters (approximate)"
    ),
    has_more: z.boolean().describe("Whether there are more pages to retrieve"),
    next_cursor: z.string().nullable().describe("Cursor to the next page of customer interactions")
  })
});
function encodeCopilotResourceId(companyId, teammateId) {
  return `company:${companyId}:teammate:${teammateId}`;
}
function decodeCopilotResourceId(resourceId) {
  const match = resourceId.match(/^company:([^:]+):teammate:([^:]+)$/);
  if (!match) {
    return void 0;
  }
  const [, companyPart, teammatePart] = match;
  if (companyPart === void 0 || teammatePart === void 0) {
    return void 0;
  }
  return {
    companyId: asCompanyId(companyPart),
    teammateId: asTeammateId(teammatePart)
  };
}
const getKnowledgeDocumentToolOutputSchema = z.object({
  document: z.object({
    // Document names are slugified titles and nothing keeps titles unique,
    // so the id names the document that was actually served — the one a
    // follow-up edit must target.
    id: z.string().describe("The retrieved document's id"),
    title: z.string().describe("The title of the retrieved document"),
    content: z.string().describe("The document content formatted as markdown")
  }).nullable().describe("The retrieved knowledge base document, or null if not found"),
  available_documents: z.array(z.string()).optional().describe(
    "The document names available in scope; present only when the requested name didn't match any document."
  )
});
const getCoachingNotesToolInputSchema = z.object({
  teammate_id: teammateIdSchema.describe(
    "The teammate ID (UUID) whose coaching notes to retrieve. Can use the teammate ID from the system context."
  ),
  period_id: z.string().nullable().describe(
    "Period ID for pagination, or null to retrieve the most recent coaching notes."
  )
});
const getCoachingNotesToolOutputSchema = z.object({
  coaching_notes: z.object({
    period_id: z.string().describe("The period ID for these coaching notes"),
    previous_period_id: z.string().nullable().describe(
      "The period ID of the previous coaching notes, for pagination. Null if no earlier notes exist."
    ),
    date_range: z.object({
      from: z.string().describe("Start date of the coaching period (ISO)"),
      to: z.string().describe("End date of the coaching period (ISO)")
    }),
    generated_at: z.string().describe("When these coaching notes were generated (ISO)"),
    criteria: z.array(
      z.object({
        criterion_id: z.uuid().describe("The criterion ID (UUID)"),
        criterion_name: z.string().describe("Human-readable criterion name"),
        grading_type: z.enum(["yes_no", "1_5_score"]).describe("How this criterion is graded"),
        teammate_average_score: z.number().nullable().describe("The teammate's average score for this criterion"),
        company_average_score: z.number().describe("The company-wide average score for this criterion"),
        previous_teammate_average_score: z.number().nullable().describe(
          "The teammate's previous period average score, null if not available"
        ),
        outcome_correlation_auc: z.number().nullable().describe(
          "AUC measuring how predictive this criterion is of positive outcomes"
        ),
        summary: z.string().describe("A brief summary of the coaching feedback (3-15 words)"),
        coaching_content: z.string().describe(
          'The full coaching content as Markdown, with <quote interaction_id="...">, <customer interaction_id="...">, <person>, and <teammate id="..."> tags for references'
        )
      })
    )
  }).nullable().describe(
    "The coaching notes for the teammate, or null if no coaching notes exist"
  )
});
export {
  COPILOT_ID,
  decodeCopilotResourceId,
  encodeCopilotResourceId,
  getCoachingNotesToolInputSchema,
  getCoachingNotesToolOutputSchema,
  getCriterionDetailsToolInputSchema,
  getCriterionDetailsToolOutputSchema,
  getKnowledgeDocumentToolOutputSchema,
  getMastraThreadLangfuseSessionId,
  getSignalDetailsToolInputSchema,
  getSignalDetailsToolOutputSchema,
  listCustomerInteractionsFiltersSchema,
  listCustomerInteractionsToolDefaultPaginationLimit,
  listCustomerInteractionsToolInputSchema,
  listCustomerInteractionsToolMaxPaginationLimit,
  listCustomerInteractionsToolOutputSchema,
  listTeammateCustomerInteractionsFiltersSchema,
  listTeammateCustomerInteractionsToolInputSchema,
  parseCopilotContent,
  searchTeammatesToolInputSchema,
  searchTeammatesToolOutputSchema
};
//# sourceMappingURL=copilot.js.map
